import { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";
import { getAuth } from "firebase/auth";
import {
  User,
  Briefcase,
  MapPin,
  Save,
  ArrowLeft,
  Loader2,
  Eye,
  EyeOff
} from "lucide-react";

import { AuthContext } from "../context/AuthContext";

function AddEmployee() {
  const { id } = useParams();
  const { orgSlug, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [showPassword, setShowPassword] = useState(false);

  const [form, setForm] = useState({
  fullName: "",
  email: "",
  password: "",
  phone: "",
  department: "",
  designation: "",
  qualification: "",
  state: "",
  district: "",
  pincode: "",
  country: ""   
});

  useEffect(() => {
    if (id) fetchEmployeeData();
  }, [id]);

  const fetchEmployeeData = async () => {
    setFetching(true);
    try {
      const res = await API.get(`employees/${id}/`);
      const d = res.data;

      setForm({
        fullName: d.personal?.fullName || "",
        email: d.email || "",
        phone: d.personal?.phone || "",
        department: d.professional?.department || "",
        designation: d.professional?.designation || "",
        qualification: d.professional?.qualification || "",
        state: d.address?.state || "",
        district: d.address?.district || "",
        country: d.address?.country || "",
        pincode: d.address?.pincode || ""
      });
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setFetching(false);
    }
  };

  if (authLoading || !orgSlug) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken(true);

      const endpoint = id ? `employees/${id}/update/` : "create-employee/";
      const method = id ? "patch" : "post";

      await API[method](endpoint, form, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(
        id
          ? "Profile updated successfully ✅"
          : "Employee created successfully ✅"
      );

      navigate(`/${orgSlug}/employees`);
    } catch (err) {
      alert(err.response?.data?.error || "Update failed.");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" size={40} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 animate-in fade-in duration-500">

      {/* Back Button */}
      <button
        onClick={() => navigate(`/${orgSlug}/employees`)}
        className="flex items-center gap-2 text-slate-500 font-bold mb-6 hover:text-indigo-600 transition"
      >
        <ArrowLeft size={20} /> Back to Directory
      </button>

      <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden">

        {/* Header */}
        <div className="bg-slate-900 p-10 text-white flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-black tracking-tight">
              {id ? "Edit Employee Profile" : "Add New Employee"}
            </h1>
            <p className="text-slate-400 mt-1">
              Manage employee information securely
            </p>
          </div>

          <div className="hidden md:block bg-white/10 p-4 rounded-2xl">
            <User className="text-indigo-400" size={32} />
          </div>
        </div>

        <div className="flex flex-col md:flex-row min-h-[500px]">

          {/* Sidebar */}
          <div className="w-full md:w-64 bg-slate-50 p-6 border-r border-slate-100 space-y-3">

            <TabBtn
              active={activeTab === "personal"}
              onClick={() => setActiveTab("personal")}
              icon={<User size={18} />}
              label="Identity"
            />

            <TabBtn
              active={activeTab === "professional"}
              onClick={() => setActiveTab("professional")}
              icon={<Briefcase size={18} />}
              label="Career"
            />

            <TabBtn
              active={activeTab === "address"}
              onClick={() => setActiveTab("address")}
              icon={<MapPin size={18} />}
              label="Location"
            />

          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 p-10 space-y-8">

            {/* PERSONAL */}
            {activeTab === "personal" && (
              <div className="grid md:grid-cols-2 gap-8">

                <Input
                  label="Full Name"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  required
                />

                <Input
                  label="Email Address"
                  name="email"
                  type="email"
                  value={form.email}
                  disabled={!!id}
                  onChange={handleChange}
                  required
                />

                {!id && (
                  <PasswordInput
                    label="Password"
                    name="password"
                    value={form.password}
                    showPassword={showPassword}
                    setShowPassword={setShowPassword}
                    onChange={handleChange}
                  />
                )}

                <Input
                  label="Phone"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                />

              </div>
            )}

            {/* PROFESSIONAL */}
            {activeTab === "professional" && (
              <div className="grid md:grid-cols-2 gap-8">

                <Input
                  label="Department"
                  name="department"
                  value={form.department}
                  onChange={handleChange}
                />

                <Input
                  label="Designation"
                  name="designation"
                  value={form.designation}
                  onChange={handleChange}
                />

                <Input
                  label="Qualification"
                  name="qualification"
                  value={form.qualification}
                  onChange={handleChange}
                />

              </div>
            )}

            {/* ADDRESS */}
            {activeTab === "address" && (
              <div className="grid md:grid-cols-2 gap-8">

                <Input
                  label="State"
                  name="state"
                  value={form.state}
                  onChange={handleChange}
                />

                <Input
                  label="District"
                  name="district"
                  value={form.district}
                  onChange={handleChange}
                />
  <Input label="Country" name="country" value={form.country} onChange={handleChange} />

                <Input
                  label="Pincode"
                  name="pincode"
                  value={form.pincode}
                  onChange={handleChange}
                />

              </div>
            )}

            {/* SUBMIT */}
            <div className="pt-10 border-t border-slate-100 flex justify-end">

              <button
                type="submit"
                disabled={loading}
                className="bg-indigo-600 text-white px-12 py-4 rounded-2xl font-black shadow-lg hover:scale-105 transition flex items-center gap-3"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Save Record
                  </>
                )}
              </button>

            </div>

          </form>

        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ active, onClick, icon, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl font-bold transition ${
      active
        ? "bg-white text-indigo-600 shadow border-l-4 border-indigo-600"
        : "text-slate-400 hover:bg-slate-200"
    }`}
  >
    {icon} {label}
  </button>
);

const Input = ({ label, ...props }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
      {label}
    </label>

    <input
      {...props}
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 focus:bg-white focus:border-indigo-500 outline-none font-bold text-slate-700 disabled:opacity-50"
    />
  </div>
);

const PasswordInput = ({
  label,
  name,
  value,
  showPassword,
  setShowPassword,
  onChange
}) => (
  <div className="space-y-2 relative">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
      {label}
    </label>

    <input
      type={showPassword ? "text" : "password"}
      name={name}
      value={value}
      onChange={onChange}
      required
      className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 pr-12 focus:bg-white focus:border-indigo-500 outline-none font-bold text-slate-700"
    />

    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="absolute right-4 top-11 text-slate-400 hover:text-indigo-600"
    >
      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
    </button>
  </div>
);

export default AddEmployee;